import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { branches } from '../data/branches';
import { services } from '../data/services';
import { useLanguage } from '../context/LanguageContext';

const AppointmentForm = ({ preselectedBranch = '', preselectedService = '', preselectedDoctor = '' }) => {
  const { t, getBranch, getService } = useLanguage();

  const getResolvedService = (serv) => {
    if (!serv) return '';
    const match = services.find(
      s => s.id.toLowerCase() === serv.toLowerCase() || s.name.toLowerCase() === serv.toLowerCase()
    );
    return match ? match.id : serv;
  };

  const getResolvedBranch = (br) => {
    if (!br) return '';
    const match = branches.find(
      b => b.slug.toLowerCase() === br.toLowerCase() || b.name.toLowerCase() === br.toLowerCase()
    );
    return match ? match.slug : br;
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    branch: getResolvedBranch(preselectedBranch),
    doctor: preselectedDoctor || '',
    otherDoctor: '',
    service: getResolvedService(preselectedService),
    date: '',
    time: '',
    message: ''
  });

  useEffect(() => {
    if (preselectedBranch) {
      setFormData(prev => ({ ...prev, branch: getResolvedBranch(preselectedBranch) }));
    }
  }, [preselectedBranch]);

  useEffect(() => {
    if (preselectedService) {
      setFormData(prev => ({ ...prev, service: getResolvedService(preselectedService) }));
    }
  }, [preselectedService]);

  useEffect(() => {
    if (preselectedDoctor) {
      setFormData(prev => ({ ...prev, doctor: preselectedDoctor }));
    }
  }, [preselectedDoctor]);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Appointment requested:', formData);
    alert(t('appointmentSection.successMsg'));
    setFormData({
      name: '', phone: '', email: '', branch: '', doctor: '', otherDoctor: '', service: '', date: '', time: '', message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 md:p-10 rounded-2xl shadow-xl border border-gray-100 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full -z-10"></div>
      
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-navy mb-2">
          {t('appointmentSection.formTitle')}
        </h2>
        <p className="text-gray-600">
          {t('appointmentSection.formSubtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.fullName')}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all"
            placeholder={t('contactSection.namePlaceholder')}
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.phone')}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all"
            placeholder={t('contactSection.phonePlaceholder')}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.email')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all"
            placeholder="example@domain.com"
          />
        </div>
        <div>
          <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.selectBranch')}
          </label>
          <select
            id="branch"
            name="branch"
            required
            value={formData.branch}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all bg-white"
          >
            <option value="">{t('appointmentSection.chooseBranch')}</option>
            {branches.map(branch => {
              const b = getBranch(branch);
              return (
                <option key={b.slug} value={b.slug}>
                  {b.name} - {b.city || b.area}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="doctor" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.selectDoctor')}
          </label>
          <select
            id="doctor"
            name="doctor"
            value={formData.doctor}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all bg-white"
          >
            <option value="">{t('appointmentSection.chooseDoctor')}</option>
            <option value="Dr. Ramakant Tekke">Dr. Ramakant Tekke</option>
            <option value="Dr. Vishakha Tekke">Dr. Vishakha Tekke</option>
            <option value="Other">{t('appointmentSection.otherDoctor')}</option>
          </select>
          {formData.doctor === 'Other' && (
            <input
              type="text"
              name="otherDoctor"
              value={formData.otherDoctor || ''}
              onChange={handleChange}
              placeholder={t('appointmentSection.enterDoctorName')}
              className="mt-2 w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all text-sm"
            />
          )}
        </div>
        <div>
          <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.selectService')}
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all bg-white"
          >
            <option value="">{t('appointmentSection.chooseService')}</option>
            {services.map(service => {
              const s = getService(service);
              return (
                <option key={s.id} value={s.id}>{s.name}</option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.preferredDate')}
          </label>
          <input
            type="date"
            id="date"
            name="date"
            required
            value={formData.date}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all"
          />
        </div>
        <div>
          <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
            {t('appointmentSection.preferredTime')}
          </label>
          <select
            id="time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all bg-white"
          >
            <option value="">{t('appointmentSection.chooseTime')}</option>
            <option value="morning">{t('appointmentSection.morning')}</option>
            <option value="afternoon">{t('appointmentSection.afternoon')}</option>
            <option value="evening">{t('appointmentSection.evening')}</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          {t('appointmentSection.message')}
        </label>
        <textarea
          id="message"
          name="message"
          rows={3}
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-clinical focus:border-transparent outline-none transition-all resize-none"
          placeholder={t('appointmentSection.messagePlaceholder')}
        ></textarea>
      </div>

      <button
        type="submit"
        className="w-full bg-clinical hover:bg-navy text-white px-8 py-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 text-lg shadow-md"
      >
        <Calendar size={20} />
        <span>{t('appointmentSection.submitBtn')}</span>
      </button>
    </form>
  );
};

export default AppointmentForm;
